import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import type { FormikHelpers, FormikTouched } from 'formik';

import type { ContentData } from './types';

import { useTranslation } from 'app/lang';

import { formatPhoneNumber } from '../../design-system/components/PhoneInput/phone-input-helpers';
import { CodeError, DataName, FIELD_NAME, getBodyText } from './constants';
import Content from './Content';
import { getErrorMessage, reGetCode } from './utils';

const ONLY_NUMBERS = /^\d+$/;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type CodeConfirmationProps = {
  data: AnyType;
  changeData: Dispatch<SetStateAction<AnyType>>;
  value: string;
  reSendData: {
    url: string;
    data: Record<string, string>;
  };
  error: CodeError | null;
  changeErrorState: () => void;
  disabled: boolean;
  changeVerifyData: () => void;
  formikHelpers: Partial<FormikHelpers<AnyType>>;
  touched?: FormikTouched<unknown>;
};

const CodeConfirmation: FC<CodeConfirmationProps> = ({
  data,
  changeData,
  value,
  reSendData,
  error,
  changeErrorState,
  disabled,
  changeVerifyData,
  formikHelpers,
  touched,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, setTouched } = formikHelpers;
  const { requisite, resendDate, serverTime } = data;

  const hasPhone = ONLY_NUMBERS.test(requisite);
  const name = hasPhone ? DataName.PHONE : DataName.EMAIL;
  const formattedRequisite = hasPhone ? `+${formatPhoneNumber(requisite)}` : requisite;

  const bodyText = getBodyText(t, formattedRequisite);
  const [info, setInfo] = useState(bodyText.prompt);

  useEffect(() => {
    if (error !== null) {
      const errorMessage = getErrorMessage(error);
      setInfo(errorMessage);
    }
  }, [error]);

  const title = bodyText[name].title;
  const changeValue = bodyText[name].change;

  const prompt = !error ? bodyText.prompt : info;

  const handleFocus = useCallback(() => {
    if (setTouched) {
      setTouched({});
    }

    setInfo(bodyText.prompt);
    changeErrorState();
  }, [setTouched, error]);

  const handleChangeCode = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (setFieldValue) {
      setFieldValue(FIELD_NAME, event.target.value);
    }
  }, [setFieldValue]);

  const handleReGetCodeButton = useCallback(() => {
    if (setFieldValue) {
      setFieldValue(FIELD_NAME, '', false);
    }
    if (setTouched) {
      setTouched({});
    }

    reGetCode({ reSendData, changeData });
  }, [data.token, data.sessionToken, setFieldValue, setTouched]);

  const changeInfoText = useCallback(() => {
    setInfo(bodyText.prompt);
  }, []);

  const codeResendInfo = {
    resendDate,
    serverTime,
    onChange: changeInfoText,
    handler: handleReGetCodeButton,
  };

  const contentData: ContentData = {
    title,
    value,
    error,
    onChange: handleChangeCode,
    onFocus: handleFocus,
    disabled,
    codeResendInfo,
    changeVerifyData,
    changeValue,
    prompt,
    touched,
  };

  return (
    <Content data={contentData} />
  );
};

export default CodeConfirmation;
